const HERO_IMAGE =
  'https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?auto=format&fit=crop&w=1600&q=80';

const RELATED_GUIDES = [
  {
    slug: 'open-tennis-courts-powell-dublin-polaris-delaware-ohio',
    title: 'Open Tennis Courts Near Powell, Dublin, Polaris & Delaware',
    blurb: 'find a court once your elbow is ready to play again.',
  },
  {
    slug: 'soccer-field-injury-remedies-calf-hamstring',
    title: 'On-Field Remedies for Soccer Calf, Soleus & Hamstring Injuries',
    blurb: 'first aid for other common sports overuse injuries.',
  },
  {
    slug: 'soleus-pain-kt-tape-soccer-guide',
    title: 'Soleus Pain in Soccer: How to Use KT Tape & Keep Playing Safely',
    blurb: 'tendon and muscle load management for athletes.',
  },
];

const REFERENCE_LINKS = [
  {
    label: 'American Academy of Orthopaedic Surgeons — tennis elbow overview',
    url: 'https://orthoinfo.aaos.org/en/diseases--conditions/tennis-elbow-lateral-epicondylitis/',
  },
  {
    label: 'Tyler Twist study — eccentric exercise for lateral epicondylosis (PMC)',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC2971639/',
  },
  {
    label: 'Sports Injury Clinic — tennis elbow exercise program',
    url: 'https://www.sportsinjuryclinic.net/rehabilitation-exercises/tennis-elbow-exercises',
  },
  {
    label: 'Thera-Band FlexBar — Tyler Twist exercise guide',
    url: 'https://www.theraband.com/flexbar-exercises',
  },
];

/** Tennis elbow (lateral epicondylitis) — remedies, stretches, and rehab exercises */
export const TENNIS_ELBOW_REMEDIES_POST = {
  id: 'featured-tennis-elbow-remedies',
  slug: 'tennis-elbow-injury-remedies-exercises',
  title: 'Tennis Elbow: Remedies, Stretches & Rehab Exercises That Actually Work',
  excerpt:
    'That sharp ache on the outside of your elbow when you grip or lift? Learn what tennis elbow is, home remedies for pain relief, and a step-by-step exercise plan—from isometric holds to the evidence-backed Tyler Twist.',
  publishedAt: '2026-07-30T18:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Player Wellness',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Related training & wellness guides',
  relatedPosts: RELATED_GUIDES,
  referenceLinks: REFERENCE_LINKS,
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: 'Lateral epicondylitis', label: 'Medical name' },
        { value: '6–12 weeks', label: 'Typical rehab timeline' },
        { value: '3 phases', label: 'Isometric → Eccentric → Return' },
        { value: 'See a clinician', label: 'If pain persists 6+ weeks' },
      ],
    },
    {
      type: 'panel',
      title: 'What is tennis elbow?',
      paragraphs: [
        '**Tennis elbow** (lateral epicondylitis or lateral epicondylopathy) is pain and tenderness on the **outside** of the elbow, where the wrist extensor tendons attach to the lateral epicondyle — the bony bump on the outer elbow. Despite the name, most people who get it never pick up a racquet.',
        'It is an **overuse tendinopathy**: repeated gripping, wrist extension, and forearm rotation create tiny tears in the tendon that fail to heal because you keep loading the area. Tennis, pickleball, golf, weightlifting, typing, and even repetitive throw-ins in soccer can trigger it.',
        'The good news: research consistently shows that **progressive loading exercises** work better long-term than rest alone or corticosteroid injections. This guide covers home remedies for pain relief and a phased exercise plan you can run at home. It is not medical advice — see a physio or orthopaedic clinician if pain is severe, worsening, or lasting more than six weeks.',
      ],
      chips: ['Tennis & racquet sports', 'Home rehab', 'Evidence-based', 'Not medical advice'],
    },
    {
      type: 'table',
      title: 'Tennis elbow vs. golfer\'s elbow — know the difference',
      intro: 'Both are epicondylopathies, but the location and muscles involved differ. This guide focuses on tennis elbow (outside); golfer\'s elbow is the inside counterpart.',
      headers: ['Feature', 'Tennis elbow (lateral)', 'Golfer\'s elbow (medial)'],
      rows: [
        [
          'Where it hurts',
          'Outside of elbow — lateral epicondyle',
          'Inside of elbow — medial epicondyle',
        ],
        [
          'Muscles affected',
          'Wrist extensors (lift back of hand)',
          'Wrist flexors (curl palm toward forearm)',
        ],
        [
          'Common triggers',
          'Backhand, serve, gripping racquet, lifting with palm down',
          'Golf swing, throwing, chin-ups, hammer curls',
        ],
        [
          'Pain with',
          'Shaking hands, turning a doorknob, lifting a coffee mug',
          'Gripping, wrist flexion, throwing',
        ],
        [
          'Key rehab exercise',
          'Eccentric wrist extension (Tyler Twist)',
          'Eccentric wrist flexion (reverse Tyler Twist)',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Signs you have tennis elbow',
      list: [
        'Tenderness on the bony bump on the **outside** of the elbow.',
        'Pain that worsens when gripping — racquet, dumbbell, steering wheel, or water bottle.',
        'Weakness in the hand or wrist, especially when lifting with palm facing down.',
        'Pain that builds gradually over days or weeks rather than from a single traumatic blow.',
        'Stiffness in the forearm after activity or first thing in the morning.',
        'Pain when extending the wrist against resistance (classic clinical test).',
      ],
      callout:
        'Sudden pop, severe swelling, or inability to straighten the elbow may indicate a different injury — get examined in person.',
    },
    {
      type: 'panel',
      title: 'Home remedies for pain relief',
      intro: 'Use these in the first 1–2 weeks to calm symptoms while you prepare to start loading exercises. Remedies manage pain; exercises fix the tendon.',
      orderedList: [
        '**Rest from aggravating activity** — stop or reduce tennis, pickleball, heavy lifting, and repetitive gripping for 1–2 weeks. You do not need total arm immobilization.',
        '**Ice** — 15 minutes with a towel barrier, 3–4 times per day during the acute flare. Especially after activity that increases pain.',
        '**Compression sleeve or counterforce brace** — worn 2–3 cm below the elbow (on the forearm, not the painful bump). Reduces load on the tendon during daily tasks.',
        '**Ergonomic adjustments** — larger grip on racquet (one size up), looser string tension, or a lighter racquet reduces shock to the elbow.',
        '**Anti-inflammatory gel (topical)** — diclofenac gel applied to the outer forearm can help short-term pain. Ask a pharmacist if appropriate for you.',
        '**Sleep position** — avoid sleeping with the painful arm under your pillow or bent tightly under your body.',
      ],
    },
    {
      type: 'panel',
      title: 'Self-massage & positional release (acute phase)',
      paragraphs: [
        'Before loading the tendon with weights, gentle soft-tissue work can reduce muscle guarding around the elbow. Massage the **muscle belly** of the forearm extensors — not directly on the most painful bony spot.',
      ],
      list: [
        '**Cross-fiber friction** — two fingertips on the tender forearm muscle (not the bone), massage across the fibers for 30–60 seconds.',
        '**Positional release (90-second hold)** — find the wrist position that feels least painful (often slight flexion and rotation). Hold comfortably for a full 90 seconds while breathing slowly.',
        '**Pressure + wrist extension** — steady thumb pressure on the muscle belly while slowly extending the wrist 10 times. Stop if pain exceeds 3/10.',
      ],
      callout:
        'Deep aggressive massage on a fresh tendinopathy can increase irritation. Light pressure only in the first week.',
    },
    {
      type: 'videoList',
      title: 'Video — pain relief & self-treatment',
      videos: [
        {
          title: 'STOP Your Elbow Pain (Tennis Elbow) in 90 Seconds, Self Treatment',
          channel: 'Bob & Brad',
          description:
            'Positional release technique and gentle massage to calm acute tennis elbow pain before starting rehab exercises.',
          url: 'https://www.youtube.com/watch?v=eIdYNJRa2Hk',
        },
        {
          title: 'How to Fix Tennis Elbow! (3 Easy Steps)',
          channel: 'Bob & Brad',
          description:
            'Three-step approach: positional release, massage options, and when to progress to strengthening.',
          url: 'https://www.youtube.com/watch?v=60UuEf8IbUo',
        },
        {
          title: '7 Best Tennis Elbow Pain Relief Treatments (Lateral Epicondylitis)',
          channel: 'Ask Doctor Jo',
          description:
            'Bracing, ice, stretches, eccentric loading with an armbar, and soft-tissue mobilization — full pain-relief toolkit.',
          url: 'https://www.youtube.com/watch?v=4vk3i22z3Ko',
        },
      ],
    },
    {
      type: 'panel',
      title: 'Stretching exercises (start when pain allows)',
      intro: 'Stretch daily — before and after strengthening. Hold each stretch 15–30 seconds, 3–5 reps. Stretch to mild tension, not sharp pain.',
      list: [
        '**Wrist extensor stretch** — arm straight, palm down. Use your other hand to gently bend the wrist downward until you feel a stretch along the top of the forearm. Rotate forearm inward slightly to target the outside elbow.',
        '**Wrist flexor stretch** — arm straight, palm up. Gently pull fingers back toward you. Balances the forearm and reduces compensatory tightness.',
        '**Towel twist** — hold a towel with both hands, twist as if wringing water out. Slow and controlled, 10 twists each direction.',
        '**Forearm pronation/supination** — elbow bent 90°, rotate palm up and down slowly. 10 reps each way.',
      ],
    },
    {
      type: 'panel',
      title: 'Rehab exercises — 3-phase progression',
      paragraphs: [
        'Current sports-medicine guidelines recommend **progressive tendon loading** over rest alone. Move to the next phase only when the current phase causes no more than 3/10 pain during exercise and no spike in pain the next morning.',
      ],
    },
    {
      type: 'panel',
      title: 'Phase 1 — Isometric wrist extension (weeks 1–2)',
      intro: 'Load the tendon without moving the joint. Best starting point when gripping or lifting still hurts.',
      orderedList: [
        'Rest forearm on a table, palm down, elbow bent ~90°.',
        'Place your other hand on the back of the injured hand.',
        'Push upward into the resistance without allowing the wrist to move.',
        'Hold 30–45 seconds at moderate effort. Pain should stay at or below 3/10.',
        'Rest 30 seconds. Repeat 3–5 holds, once or twice daily.',
      ],
      callout:
        'Alternative: press the back of your hand against a wall or table edge in the same isometric position.',
    },
    {
      type: 'panel',
      title: 'Phase 2 — Eccentric wrist extension / Tyler Twist (weeks 3–8)',
      intro: 'The most evidence-backed exercise for tennis elbow. Eccentric loading means lengthening the muscle under tension — the tendon adapts and strengthens.',
      orderedList: [
        'Hold a Thera-Band FlexBar (or similar rubber bar) vertically in the affected hand, palm facing you, wrist extended.',
        'Grip the top of the bar with your other hand, palm facing away. Twist the bar with the **uninjured** hand only.',
        'Extend both arms in front of you, turning the bar horizontal while maintaining the twist.',
        'Slowly allow the injured wrist to flex back to neutral over **4 seconds** — that slow lowering is the eccentric phase.',
        'Use the good hand to help twist back to the start. **3 sets of 15 reps, once daily.**',
      ],
      list: [
        'No FlexBar? Use a light dumbbell (1–3 lbs): lift with both hands, lower slowly with the injured arm only over 4 seconds.',
        'Start with red (light) FlexBar; progress to green, then blue as strength improves.',
        'Mild discomfort during the set (up to 4/10) is acceptable. Sharp pain or next-day flare means reduce reps or resistance.',
      ],
    },
    {
      type: 'panel',
      title: 'Phase 3 — Concentric strengthening & return to sport (weeks 6–12)',
      intro: 'Once eccentrics feel manageable, add full-range wrist extension and grip work.',
      list: [
        '**Wrist extension with dumbbell** — forearm on table, palm down. Lift a light weight (1–5 lbs) up and lower slowly. 3×15.',
        '**Reverse wrist curls** — palm up, curl weight upward. Balances forearm strength.',
        '**Grip strengthening** — squeeze a tennis ball or therapy putty. 2×20 squeezes.',
        '**Forearm pronation/supination with weight** — hold a hammer or light dumbbell, rotate slowly. 2×15 each direction.',
        '**Sport-specific drills** — shadow swings, light rallying, or progressive throw-ins before full match play.',
      ],
    },
    {
      type: 'videoList',
      title: 'Video — rehab exercises & Tyler Twist',
      videos: [
        {
          title: 'Thera-Band Flexbar "Tyler Twist" exercise for Tennis Elbow',
          channel: 'Dr. Phil Page',
          description:
            'Official demonstration of the research-backed Tyler Twist eccentric exercise — the gold standard for tennis elbow rehab.',
          url: 'https://www.youtube.com/watch?v=gsKGbqA9aNo',
        },
        {
          title: 'Elbow Isometric Exercises | Tennis Elbow | Theraband Flexbar',
          channel: 'Physio channel',
          description:
            'Isometric wrist extension with dumbbell, flat band, and FlexBar — start here before eccentrics.',
          url: 'https://www.youtube.com/watch?v=J7UQhirvUPc',
        },
        {
          title: 'Fix Elbow Pain, One Simple Self Treatment (90 Seconds)',
          channel: 'Bob & Brad',
          description:
            'Quick positional release for both tennis and golfer\'s elbow — useful during acute flares.',
          url: 'https://www.youtube.com/watch?v=s4xCKEr0LkA',
        },
      ],
    },
    {
      type: 'table',
      title: 'What NOT to do with tennis elbow',
      headers: ['Mistake', 'Why it makes things worse'],
      rows: [
        [
          'Complete rest for months',
          'Tendons weaken without loading; pain returns the moment you grip again',
        ],
        [
          'Cortisone injection as first treatment',
          'Short-term relief but worse outcomes at 12 months vs. exercise in studies',
        ],
        [
          'Aggressive stretching into sharp pain',
          'Irritates the tendon in the acute phase',
        ],
        [
          'Playing through 7/10 pain every match',
          'Prevents tendon healing; turns a 6-week problem into a 6-month one',
        ],
        [
          'Heavy lifting with palm-down grip',
          'Maximum load on wrist extensors — the injured tissue',
        ],
        [
          'Ignoring grip size and racquet weight',
          'Small grips and heavy frames multiply shock at the elbow',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Return to tennis, pickleball, or sport checklist',
      list: [
        'Daily tasks (opening jars, carrying bags) pain-free for 5+ days.',
        '25 eccentric Tyler Twist reps at your working resistance without next-day flare.',
        'Grip strength within 90% of the uninjured side (compare tennis ball squeeze hold time).',
        'Shadow swings or practice strokes pain-free for 10 minutes.',
        'Light rallying or modified play for 20 minutes — no pain spike within 24 hours.',
        'Full match or session only after 2–3 successful light sessions.',
        'Continue eccentric exercises 2–3× per week even after return — maintenance prevents relapse.',
      ],
      callout:
        'Most rec players relapse because they stop rehab the day pain disappears. Keep loading the tendon for at least 12 weeks total.',
    },
    {
      type: 'panel',
      title: 'When to see a doctor or physio',
      list: [
        'Pain lasting more than 6 weeks despite consistent home exercises.',
        'Pain that wakes you at night or is constant at rest.',
        'Numbness or tingling in the fingers (may indicate nerve involvement).',
        'Elbow swelling, redness, or warmth (rule out infection or arthritis).',
        'Sudden loss of grip strength or inability to fully extend the elbow.',
        'No improvement after 4 weeks of daily eccentric loading at correct dose.',
        'You need a formal diagnosis before returning to competitive play.',
      ],
    },
    {
      type: 'panel',
      title: 'Prevention — keep tennis elbow from coming back',
      list: [
        'Warm up forearms before every match: wrist circles, extensor stretches, light shadow swings.',
        'Use the correct grip size — too small forces excessive grip tension.',
        'Check string tension; overly tight strings transmit more shock to the elbow.',
        'Add eccentric wrist extension 2× per week year-round, not just during rehab.',
        'Strengthen shoulder and scapular muscles — poor shoulder mechanics overload the elbow.',
        'Alternate training days; avoid back-to-back hard sessions on consecutive days.',
        'Replace worn grips on racquets and gym equipment regularly.',
      ],
    },
  ],
};
